import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  loadPersisted,
  readLegacyZustand,
  savePersisted,
} from '~utils/persistedState';

export type LastModifiedStoreEntry = {
  id: string;
  url: string;
  name: string;
  path: string;
  icon: string;
  type: 'Subscription' | 'Resource group' | 'Site' | 'Device register';
};

export type LastModifiedState = {
  entries: Record<string, LastModifiedStoreEntry[]>;
};

const LAST_MODIFIED_STORAGE_KEY = 'last-modified';
const MAX_ENTRIES = 5;

function loadInitialState(): LastModifiedState {
  return loadPersisted(
    LAST_MODIFIED_STORAGE_KEY,
    readLegacyZustand('last-modified-store', { entries: {} })
  );
}

type LastModifiedContextValue = {
  entries: Record<string, LastModifiedStoreEntry[]>;
  insertEntry: (tenantId: string, entry: LastModifiedStoreEntry) => void;
  removeEntry: (tenantId: string, id: string) => void;
};

const LastModifiedContext = createContext<LastModifiedContextValue | undefined>(
  undefined
);

function persist(state: LastModifiedState) {
  savePersisted(LAST_MODIFIED_STORAGE_KEY, state);
}

// Imperative API for use outside React (e.g. Redux thunks).
// Connected by the provider on mount so thunks can call insertEntry/removeEntry
// without needing React context access.
let _imperativeApi: {
  insertEntry: (tenantId: string, entry: LastModifiedStoreEntry) => void;
  removeEntry: (tenantId: string, id: string) => void;
} | null = null;

export function getLastModifiedApi() {
  return _imperativeApi;
}

export function LastModifiedProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LastModifiedState>(loadInitialState);

  const insertEntry = useCallback(
    (tenantId: string, entry: LastModifiedStoreEntry) => {
      setState(prev => {
        const existing = prev.entries[tenantId] || [];
        const next: LastModifiedState = {
          entries: {
            ...prev.entries,
            [tenantId]: [
              entry,
              ...existing.filter(e => e.id !== entry.id),
            ].slice(0, MAX_ENTRIES),
          },
        };
        persist(next);
        return next;
      });
    },
    []
  );

  const removeEntry = useCallback((tenantId: string, id: string) => {
    setState(prev => {
      const existing = prev.entries[tenantId] || [];
      const next: LastModifiedState = {
        entries: {
          ...prev.entries,
          [tenantId]: existing.filter(e => e.id !== id),
        },
      };
      persist(next);
      return next;
    });
  }, []);

  // Expose the insert/remove functions to imperative (non-React) callers.
  useEffect(() => {
    _imperativeApi = { insertEntry, removeEntry };
    return () => {
      _imperativeApi = null;
    };
  }, [insertEntry, removeEntry]);

  const value = useMemo(
    () => ({ entries: state.entries, insertEntry, removeEntry }),
    [state.entries, insertEntry, removeEntry]
  );

  return (
    <LastModifiedContext.Provider value={value}>
      {children}
    </LastModifiedContext.Provider>
  );
}

export function useLastModified(): LastModifiedContextValue {
  const ctx = useContext(LastModifiedContext);
  if (!ctx)
    throw new Error('useLastModified must be used within LastModifiedProvider');
  return ctx;
}
